import { twMerge } from 'tailwind-merge';
import type { RevenueReport } from '@witmetrics/utils';
import { usePracticeCurrency } from '@witmetrics/data-store';
import { formatNumber, formatPrice } from '@witmetrics/utils/strings';
import OverviewCard from '@/components/OverviewCard';
import Table, { TableBody, TableCell, TableRow } from '@/components/Table';
import SalesTableHead from './SalesTableHead';
import {
  classes,
  exportReport,
  formatTreatmentLabel,
  groupTreatmentByMonth,
  styles,
  sumValues,
  sumValuesByMonth,
} from './utils';

export type TreatmentRevenueCardProps = {
  isFetchingData?: boolean;
  currentReport: RevenueReport | null;
  previousReport: RevenueReport | null;
};

export default function TreatmentRevenueCard({
  isFetchingData,
  currentReport,
  previousReport,
}: TreatmentRevenueCardProps) {
  const currency = usePracticeCurrency();

  const renderRevenueRows = () => {
    return groupedData.map((treatment, i) => (
      <TableRow key={treatment.id} hover>
        <TableCell className={classes.stickyCell}>
          {formatTreatmentLabel(treatment)}
        </TableCell>
        {treatment.revenue.map((revenue, j) => (
          <TableCell
            key={j}
            // className={getCellClasses(compareData[i]?.revenue[j])}>
            className={classes.cell}>
            {revenue === 0 ? '--' : formatPrice(revenue, currency)}
          </TableCell>
        ))}
        <TableCell className={classes.summaryCell}>
          {formatPrice(sumValues(treatment.revenue), currency)}
        </TableCell>
      </TableRow>
    ));
  };

  const renderCountRows = () => {
    return groupedData.map((treatment, i) => (
      <TableRow key={treatment.id} hover>
        <TableCell className={classes.stickyCell}>
          {formatTreatmentLabel(treatment)}
        </TableCell>
        {treatment.count.map((count, j) => (
          <TableCell
            key={j}
            // className={getCellClasses(compareData[i]?.count[j])}>
            className={classes.cell}>
            {count === 0 ? '--' : formatNumber(count)}
          </TableCell>
        ))}
        <TableCell className={classes.summaryCell}>
          {formatNumber(sumValues(treatment.count))}
        </TableCell>
      </TableRow>
    ));
  };

  const groupedData = groupTreatmentByMonth(currentReport);
  const totalRevenue = sumValuesByMonth(groupedData, 'revenue');
  const totalCount = sumValuesByMonth(groupedData, 'count');

  return (
    <OverviewCard
      isFetchingData={isFetchingData}
      title="Treatments Overview"
      subtitle="Treatment performance per month"
      onExport={() => exportReport(currentReport)}>
      {currentReport && (
        <Table stickyHeader className={classes.table}>
          <SalesTableHead year={2026} />
          <TableBody sx={styles.tableBody}>
            {renderRevenueRows()}
            <TableRow hover>
              <TableCell
                className={twMerge(classes.summaryCell, classes.stickyCell)}>
                Total
              </TableCell>
              {totalRevenue.map((revenue, i) => (
                <TableCell key={i} className={classes.summaryCell}>
                  {revenue === 0 ? '--' : formatPrice(revenue, currency)}
                </TableCell>
              ))}
              <TableCell className={classes.summaryCell}>
                {formatPrice(sumValues(totalRevenue), currency)}
              </TableCell>
            </TableRow>
            <SalesTableHead row year={2026} />
            {renderCountRows()}
            <TableRow hover>
              <TableCell
                className={twMerge(classes.summaryCell, classes.stickyCell)}>
                Total
              </TableCell>
              {totalCount.map((count, i) => (
                <TableCell key={i} className={classes.summaryCell}>
                  {count === 0 ? '--' : formatNumber(count)}
                </TableCell>
              ))}
              <TableCell className={classes.summaryCell}>
                {formatNumber(sumValues(totalCount))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
    </OverviewCard>
  );
}
